/**
 * The three things on this page that belong to whoever runs the game, not to
 * whoever wrote it: the company in the footer, the social links beside it, and
 * the legal documents behind them.
 *
 * All three are admin-managed — `GET /api/site-info` and `GET /api/cms/*`, both
 * public — so the copyright line, the registered address, the support address
 * and the terms are edited in the admin panel rather than in this repo. A
 * landing page with a hard-coded company name is a landing page that has to be
 * redeployed when the company moves.
 *
 * ## Deliberately not `net/api.ts`
 *
 * That module's `request()` treats a 401 as "refresh the session and retry".
 * These are public reads made by a visitor who has no session at all, so a
 * server that answered 401 would send the page off to `POST /api/auth/refresh`
 * for nothing, on the one screen where the visitor is provably not signed in.
 * Plain `fetch`, and every failure resolves to nothing rather than rejecting —
 * the footer hides what it has no data for. A CMS that is down must not be able
 * to stop the Play button being reachable.
 */
import type { LegalDoc as LegalListEntry } from '../state/legal-slugs';

const BASE = import.meta.env.VITE_API_URL || '';

export interface CompanyInfo {
  name: string;
  email: string;
  address: string;
  registration: string;
  gameName: string;
}

export interface SocialLink {
  platform: string;
  url: string;
}

/**
 * One enabled payment method, as `/api/site-info` sends it — `slug` matches
 * the icon filename (`/payment-methods/{icon}.svg` ships all 88 of them as
 * static assets; see `payment-methods.constants.ts` in core), `name` is the
 * accessible label. Only *enabled* methods are ever sent — the admin panel's
 * toggle list is what turns one on, and every entry starts disabled.
 */
export interface PaymentMethodInfo {
  slug: string;
  name: string;
  icon: string;
}

export interface LegalDoc {
  slug: string;
  title: string;
  /**
   * Admin-authored HTML, authored through Quill in the admin panel. Rendered
   * with `v-html="sanitizeHtml(doc.content)"` — see `LegalOverlay.vue` — never
   * trusted raw, because it reaches every visitor's browser regardless of who
   * wrote it.
   */
  content: string;
}

async function getJson<T>(path: string): Promise<T | null> {
  try {
    const response = await fetch(`${BASE}${path}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

interface SiteInfoResponse {
  company?: Partial<CompanyInfo> & { registration?: string; game_name?: string };
  socialLinks?: { platform?: string; url?: string }[];
  paymentMethods?: { slug?: string; name?: string; icon?: string }[];
}

/**
 * Cached at module scope, not component scope — the same discipline
 * `cachedShowcaseSpecs` below applies, and for the identical reason:
 * `LandingScreen` (and therefore its child `SiteFooter`) remounts every time
 * a visitor bounces from the sign-in form back to `#top`, and the company
 * block and social row cannot have changed in between, so a second mount
 * reusing the first mount's answer is a cache hit, not a stale one. A
 * *failed* fetch is deliberately not cached, so a transient miss stays
 * retryable the next time the page is looked at.
 */
let cachedSiteInfo: Promise<{
  company: CompanyInfo | null;
  social: SocialLink[];
  paymentMethods: PaymentMethodInfo[];
}> | null = null;

/**
 * The footer's company block, social row and payment row in one request.
 *
 * `/api/site-info` already carries all three, and it is the endpoint the
 * template intends a frontend to read at boot — so this is one round trip
 * rather than three, and `/api/cms/social-links` is left for anyone who needs
 * the ordered, icon-carrying version. Empty strings are normalised to absent
 * here so every consumer can ask one question (`is there a name`) rather than
 * two. `paymentMethods` is already filtered to enabled-only and sorted by the
 * server (`SiteInfoController`) — nothing here re-derives that.
 */
export function fetchSiteInfo(): Promise<{
  company: CompanyInfo | null;
  social: SocialLink[];
  paymentMethods: PaymentMethodInfo[];
}> {
  if (cachedSiteInfo) return cachedSiteInfo;
  cachedSiteInfo = (async () => {
    const [data, socialRows] = await Promise.all([
      getJson<SiteInfoResponse>('/api/site-info'),
      getJson<{ platform?: string; url?: string }[]>('/api/cms/social-links'),
    ]);
    if (!data) {
      cachedSiteInfo = null;
      return { company: null, social: [], paymentMethods: [] };
    }

    const c = data.company ?? {};
    const name = (c.name ?? '').trim();
    const company: CompanyInfo | null = name
      ? {
          name,
          email: (c.email ?? '').trim(),
          address: (c.address ?? '').trim(),
          registration: (c.registration ?? '').trim(),
          gameName: (c.gameName ?? '').trim() || 'Steel of War',
        }
      : null;

    // The admin's Social links list (active only, in the operator's order) —
    // `site-info.socialLinks` reads a different, unused settings store.
    const social = (Array.isArray(socialRows) ? socialRows : [])
      .map((l) => ({ platform: (l.platform ?? '').trim(), url: (l.url ?? '').trim() }))
      .filter((l) => l.url.length > 0 && /^https?:\/\//i.test(l.url));

    const paymentMethods = (data.paymentMethods ?? [])
      .map((m) => ({
        slug: (m.slug ?? '').trim(),
        name: (m.name ?? '').trim(),
        icon: (m.icon ?? '').trim(),
      }))
      .filter((m) => m.slug.length > 0);

    return { company, social, paymentMethods };
  })();
  return cachedSiteInfo;
}

/** One legal document, or `null` when the admin has not published it. */
export function fetchLegalDoc(slug: string): Promise<LegalDoc | null> {
  return getJson<LegalDoc>(`/api/cms/legal/${encodeURIComponent(slug)}`);
}

/**
 * Every published legal document, in the order the server lists them —
 * `GET /api/legal`, this game's own public read (see `ADMIN_API_CHANGELOG.md`'s
 * 2026-09-03 entry). Replaces the old hardcoded three-slug list: an operator
 * can add a document from the admin panel's Legal section and it appears here
 * without a deploy, the same "everything visible degrades" rule as the rest of
 * this file — a down or empty CMS resolves to an empty list, not an error.
 */
export async function fetchLegalList(): Promise<LegalListEntry[]> {
  const data = await getJson<Array<{ slug?: string; title?: string }>>('/api/legal');
  if (!Array.isArray(data)) return [];
  return data
    .map((d) => ({ slug: (d.slug ?? '').trim(), title: (d.title ?? '').trim() }))
    .filter((d) => d.slug.length > 0);
}

/**
 * A news post's shared fields — everything a preview card needs: thumbnail,
 * title, introduction and a display date. `createdAt` rides along
 * unrendered so `newsDisplayDate` has something to fall back to for a post
 * published immediately, with no explicit date set.
 */
export interface NewsSummary {
  id: string;
  slug: string;
  title: string;
  summary: string;
  imageUrl: string | null;
  tag: string;
  publishedAt: string | null;
  createdAt: string;
}

/** The full article, body included. */
export interface NewsArticle extends NewsSummary {
  content: string;
}

export interface NewsPage {
  /** The two most recently published posts, always sent — see `fetchNews`. */
  featured: NewsSummary[];
  /** Everything else, paginated, with the featured pair excluded. */
  items: NewsSummary[];
  page: number;
  pageSize: number;
  total: number;
}

const EMPTY_NEWS_PAGE_SIZE = 9;

function emptyNewsPage(page: number): NewsPage {
  return { featured: [], items: [], page, pageSize: EMPTY_NEWS_PAGE_SIZE, total: 0 };
}

/**
 * The landing page's news feed — `GET /api/news`, this game's own public
 * read over the admin-managed `News` table (`NewsPublicController`, game-side
 * rather than core's `/api/cms/news` because it is the one that actually
 * honours a scheduled `publishedAt`; see that file's header). `featured` is
 * the two newest visible posts and is sent on every page so a caller never
 * has to special-case page one; `items` is the paginated rest.
 *
 * A malformed or failed response degrades to an empty page — same rule as
 * every other fetch in this file: a down CMS must not block the section.
 */
export async function fetchNews(page = 1): Promise<NewsPage> {
  const data = await getJson<Partial<NewsPage>>(`/api/news?page=${page}`);
  if (!data) return emptyNewsPage(page);
  return {
    featured: Array.isArray(data.featured) ? data.featured : [],
    items: Array.isArray(data.items) ? data.items : [],
    page: typeof data.page === 'number' ? data.page : page,
    pageSize: typeof data.pageSize === 'number' ? data.pageSize : EMPTY_NEWS_PAGE_SIZE,
    total: typeof data.total === 'number' ? data.total : 0,
  };
}

/** One article, body included — `null` if the slug doesn't resolve to a public post. */
export function fetchNewsArticle(slug: string): Promise<NewsArticle | null> {
  return getJson<NewsArticle>(`/api/news/${encodeURIComponent(slug)}`);
}

/**
 * A post's display date: `publishedAt` when it has one, `createdAt`
 * otherwise (an immediately-published post with no explicit date still
 * needs something to show). Always formatted, never a raw ISO string.
 */
export function newsDisplayDate(post: { publishedAt: string | null; createdAt: string }): string {
  const date = new Date(post.publishedAt ?? post.createdAt);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * One FAQ entry, as the "Before you enlist" section needs it — the same
 * shape as `landing-content.ts`'s own `Question` interface, so the template
 * binding stayed unchanged when the section switched from that file's
 * hardcoded `QUESTIONS` array to this fetch.
 */
export interface FaqEntry {
  question: string;
  answer: string;
}

/**
 * Cached at module scope, same discipline as `cachedSiteInfo` above: the FAQ
 * section is not going to answer differently the second time this page
 * mounts in one tab, and a failed fetch is deliberately not cached so a
 * transient miss stays retryable.
 */
let cachedFaq: Promise<FaqEntry[]> | null = null;

/**
 * The active, ordered FAQ rows an admin has published — `GET /api/cms/faq`
 * (`CmsPublicController`, core, unmodified: already filters to `active` and
 * orders by `sortOrder`). Same "everything visible degrades" rule as the rest
 * of this file: a down or empty CMS resolves to an empty list, and the
 * section that reads it renders nothing rather than throwing.
 */
export function fetchFaq(): Promise<FaqEntry[]> {
  if (cachedFaq) return cachedFaq;
  cachedFaq = (async () => {
    const data = await getJson<Array<{ question?: string; answer?: string }>>('/api/cms/faq');
    if (!Array.isArray(data)) {
      cachedFaq = null;
      return [];
    }
    return data
      .map((d) => ({ question: (d.question ?? '').trim(), answer: (d.answer ?? '').trim() }))
      .filter((d) => d.question.length > 0 && d.answer.length > 0);
  })();
  return cachedFaq;
}

export interface ShowcaseSpecRow {
  hullId: string;
  turretId: string;
  maxHealth: number;
  damage: number;
  maxForwardSpeed: number;
  reloadSeconds: number;
}

/**
 * Cached at module scope, not component scope. `LandingScreen` remounts
 * every time a visitor bounces from the sign-in form back to `#top` — see
 * `App.vue`'s `authView` — and this is the same ten pairs every time, so a
 * second mount reusing the first mount's answer is a cache hit, not a stale
 * one. A *failed* fetch is deliberately not cached, the same discipline
 * `model-thumbnail.ts` applies to its own render cache: a transient miss
 * should be retryable the next time the page is looked at, not permanent for
 * the rest of the tab's life.
 */
let cachedShowcaseSpecs: Promise<ShowcaseSpecRow[] | null> | null = null;

/**
 * Base, level-zero stats for a set of hull/turret pairs — real "HP / damage /
 * speed / reload" numbers for the tank showcase's stat bars, off the one
 * corner of the garage that is genuinely public
 * (`GET /api/garage/catalogue/spec`, see `GarageCatalogueController`).
 *
 * Same "everything visible degrades" rule as the rest of this file: a
 * failure resolves to `null`, and the showcase's cards fall back to no bars
 * at all rather than blocking on the request.
 */
export function fetchShowcaseSpecs(
  pairs: readonly { hullId: string; turretId: string }[],
): Promise<ShowcaseSpecRow[] | null> {
  if (cachedShowcaseSpecs) return cachedShowcaseSpecs;
  const query = pairs.map((p) => `${p.hullId}:${p.turretId}`).join(',');
  cachedShowcaseSpecs = getJson<ShowcaseSpecRow[]>(
    `/api/garage/catalogue/spec?pairs=${encodeURIComponent(query)}`,
  ).then((result) => {
    if (!result) cachedShowcaseSpecs = null;
    return result;
  });
  return cachedShowcaseSpecs;
}
